import type { MeasurementData } from '../store/reducers/workspace.reducer';
import type { IesCandelaGrid } from '../types/lampWireframes';
import { type LuminairePlotSource, luminairePlotSourceCacheKey } from '../utils/pdfPlotGeneration/loadLuminaireGrids';
import type { GeneratePlotsRequest, GeneratePlotsResponse } from './pdfPlotWorker';

interface GeneratePlotsInput {
  measurementData: MeasurementData[];
  luminaireSources: LuminairePlotSource[];
  iesGridsByUuid: Map<string, IesCandelaGrid>;
}

interface GeneratePlotsHandle {
  promise: Promise<Record<string, string>>;
  cancel: () => void;
}

/**
 * Offloads PDF plot generation (measurement + LDC canvas) to a dedicated worker so the UI stays responsive.
 * IES grid fetching stays on the main thread (network).
 */
class PdfPlotWorkerManager {
  private worker: Worker | null = null;
  private pending = new Map<
    string,
    { resolve: (images: Record<string, string>) => void; reject: (e: Error) => void }
  >();
  private inFlight = new Map<string, Promise<Record<string, string>>>();
  private seq = 0;

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    try {
      this.worker = new Worker(new URL('./pdfPlotWorker.ts', import.meta.url), {
        type: 'module',
        name: 'pdf-plot-worker',
      });
      this.worker.onmessage = this.handleMessage.bind(this);
      this.worker.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('PdfPlotWorkerManager: failed to init worker:', error);
      this.worker = null;
    }
  }

  private handleMessage(event: MessageEvent<GeneratePlotsResponse>) {
    const msg = event.data;
    const entry = this.pending.get(msg.id);
    if (!entry) return;
    this.pending.delete(msg.id);

    if (msg.type === 'PLOTS_DONE') {
      entry.resolve(msg.data.images);
    } else {
      entry.reject(new Error(msg.data.error));
    }
  }

  private handleError(event: ErrorEvent) {
    const err = new Error(event.message || 'pdf plot worker error');
    this.pending.forEach((entry) => entry.reject(err));
    this.pending.clear();
  }

  private cacheKey(input: GeneratePlotsInput): string {
    const layerIds = input.measurementData
      .map((m) => m.uuid)
      .sort()
      .join(',');
    const gridKeys = [...input.iesGridsByUuid.keys()].sort().join(',');
    const sourceKeys = luminairePlotSourceCacheKey(input.luminaireSources);
    return `${layerIds}:${gridKeys}:${sourceKeys}`;
  }

  generate(input: GeneratePlotsInput): GeneratePlotsHandle {
    const key = this.cacheKey(input);
    const existing = this.inFlight.get(key);
    if (existing) {
      return { promise: existing, cancel: () => {} };
    }

    if (!this.worker) {
      let cancelled = false;
      const promise = import('../utils/pdfPlotGeneration/generateReportImages')
        .then((m) =>
          m.generateFrontendReportImages(input.measurementData, input.luminaireSources, input.iesGridsByUuid),
        )
        .then((images) => {
          if (cancelled) throw new DOMException('PDF plot generation cancelled', 'AbortError');
          return images;
        })
        .finally(() => {
          this.inFlight.delete(key);
        });

      this.inFlight.set(key, promise);
      return {
        promise,
        cancel: () => {
          cancelled = true;
          this.inFlight.delete(key);
        },
      };
    }

    const id = `plot_${(this.seq += 1)}`;
    const worker = this.worker;

    const promise = new Promise<Record<string, string>>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });

      const req: GeneratePlotsRequest = {
        type: 'GENERATE_PLOTS',
        id,
        data: {
          measurementData: input.measurementData,
          luminaireSources: input.luminaireSources,
          iesGrids: [...input.iesGridsByUuid.entries()].map(([uuid, grid]) => ({ uuid, grid })),
        },
      };
      worker.postMessage(req);
    }).finally(() => {
      this.inFlight.delete(key);
    });

    this.inFlight.set(key, promise);

    return {
      promise,
      cancel: () => {
        this.pending.delete(id);
        this.inFlight.delete(key);
      },
    };
  }
}

export const pdfPlotWorkerManager = new PdfPlotWorkerManager();
